import { Component, OnChanges, Input, SimpleChanges, Injector } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService, ApUndoableOperationService } from 'webcommon/core-ui';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import { FcItemModalContext } from '../../../models';
import { FcPharmacyClearinghouseListItemDto, FormGroupValidationRules } from 'webcommon/shared';
import { FcItemListBaseComponent } from '../../generic';

@Component({
    providers: [ApAlertService, FcListValidationService],
    selector: 'fc-pharmacy-clearinghouse-interface-details-tab',
    templateUrl: './fc-pharmacy-clearinghouse-interface-details-tab.component.html',
})
export class FcPharmacyClearinghouseInterfaceDetailsTabComponent extends FcItemListBaseComponent implements OnChanges {
    @Input() baseId: string;
    @Input() form: FormGroup;
    @Input() listItem: FcPharmacyClearinghouseListItemDto;
    @Input() isSubmitted = false;
    @Input() itemContext: FcItemModalContext;

    // Form Controls
    public connectInTestModeControl: FormControl = new FormControl(true);
    public gatewayUrlControl: FormControl = new FormControl('');
    public gatewayLoginIDControl: FormControl = new FormControl('');
    public passwordControl: FormControl = new FormControl('');
    public mailboxUrlControl: FormControl = new FormControl('');
    public mailboxNameControl: FormControl = new FormControl('');
    public mailboxPasswordControl: FormControl = new FormControl('');
    public testGatewayUrlControl: FormControl = new FormControl('');
    public testGatewayLoginIDControl: FormControl = new FormControl('');
    public testPasswordControl: FormControl = new FormControl('');
    public testMailboxUrlControl: FormControl = new FormControl('');
    public testMailboxNameControl: FormControl = new FormControl('');
    public testMailboxPasswordControl: FormControl = new FormControl('');

    public listItemDefaultValidation: FormGroupValidationRules = {
        GatewayUrl: {
            MaxLength: 32768,
        },
        GatewayLoginID: {
            MaxLength: 32768,
        },
        Password: {
            MaxLength: 32768,
        },
        MailboxUrl: {
            MaxLength: 32768,
        },
        MailboxName: {
            MaxLength: 32768,
        },
        MailboxPassword: {
            MaxLength: 32768,
        },
        TestGatewayUrl: {
            MaxLength: 32768,
        },
        TestGatewayLoginID: {
            MaxLength: 32768,
        },
        TestPassword: {
            MaxLength: 32768,
        },
        TestMailboxUrl: {
            MaxLength: 32768,
        },
        TestMailboxName: {
            MaxLength: 32768,
        },
        TestMailboxPassword: {
            MaxLength: 32768,
        },
    };

    constructor(
        injector: Injector,
        protected apUndoableOperationService: ApUndoableOperationService) {
        super(injector);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.itemContext != null) {
            this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
        }
        if (changes.form != null) {
            this.addControlsToForm();
        }
        if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
            this.setForm();
        }
    }

    private addControlsToForm(): void {
        this.form.addControl('connectInTestMode', this.connectInTestModeControl);
        this.form.addControl('gatewayUrl', this.gatewayUrlControl);
        this.form.addControl('gatewayLoginID', this.gatewayLoginIDControl);
        this.form.addControl('password', this.passwordControl);
        this.form.addControl('mailboxUrl', this.mailboxUrlControl);
        this.form.addControl('mailboxName', this.mailboxNameControl);
        this.form.addControl('mailboxPassword', this.mailboxPasswordControl);
        this.form.addControl('testGatewayUrl', this.testGatewayUrlControl);
        this.form.addControl('testGatewayLoginID', this.testGatewayLoginIDControl);
        this.form.addControl('testPassword', this.testPasswordControl);
        this.form.addControl('testMailboxUrl', this.testMailboxUrlControl);
        this.form.addControl('testMailboxName', this.testMailboxNameControl);
        this.form.addControl('testMailboxPassword', this.testMailboxPasswordControl);
    }

    public setForm(): void {
        // Connect In Test Mode Control
        this.connectInTestModeControl.setValue(this.listItem.InterfaceDetails?.TestMode);

        // PROD

        // Gateway Url Control
        this.gatewayUrlControl.setValidators(this.fcListValidation.getValidators('GatewayUrl'));
        this.gatewayUrlControl.setValue(this.listItem.InterfaceDetails?.ProductionModeSettings?.GatewayUrl);

        // Gateway Login ID Control
        this.gatewayLoginIDControl.setValidators(this.fcListValidation.getValidators('GatewayLoginID'));
        this.gatewayLoginIDControl.setValue(this.listItem.InterfaceDetails?.ProductionModeSettings?.GatewayLoginID);

        // Password Control
        this.passwordControl.setValidators(this.fcListValidation.getValidators('GatewayPassword'));
        this.passwordControl.setValue(this.listItem.InterfaceDetails?.ProductionModeSettings?.GatewayPassword);

        // Mailbox Url Control
        this.mailboxUrlControl.setValidators(this.fcListValidation.getValidators('MailboxUrl'));
        this.mailboxUrlControl.setValue(this.listItem.InterfaceDetails?.ProductionModeSettings?.MailboxUrl);

        // Mailbox Name Control
        this.mailboxNameControl.setValidators(this.fcListValidation.getValidators('MailboxName'));
        this.mailboxNameControl.setValue(this.listItem.InterfaceDetails?.ProductionModeSettings?.MailboxName);

        // Mailbox Password Control
        this.mailboxPasswordControl.setValidators(this.fcListValidation.getValidators('MailboxPassword'));
        this.mailboxPasswordControl.setValue(this.listItem.InterfaceDetails?.ProductionModeSettings?.MailboxPassword);

        // TEST

        // Gateway Url Control
        this.testGatewayUrlControl.setValidators(this.fcListValidation.getValidators('GatewayUrl'));
        this.testGatewayUrlControl.setValue(this.listItem.InterfaceDetails?.TestModeSettings?.GatewayUrl);

        // Gateway Login ID Control
        this.testGatewayLoginIDControl.setValidators(this.fcListValidation.getValidators('GatewayLoginID'));
        this.testGatewayLoginIDControl.setValue(this.listItem.InterfaceDetails?.TestModeSettings?.GatewayLoginID);

        // Password Control
        this.testPasswordControl.setValidators(this.fcListValidation.getValidators('GatewayPassword'));
        this.testPasswordControl.setValue(this.listItem.InterfaceDetails?.TestModeSettings?.GatewayPassword);

        // Mailbox Url Control
        this.testMailboxUrlControl.setValidators(this.fcListValidation.getValidators('MailboxUrl'));
        this.testMailboxUrlControl.setValue(this.listItem.InterfaceDetails?.TestModeSettings?.MailboxUrl);

        // Mailbox Name Control
        this.testMailboxNameControl.setValidators(this.fcListValidation.getValidators('MailboxName'));
        this.testMailboxNameControl.setValue(this.listItem.InterfaceDetails?.TestModeSettings?.MailboxName);

        // Mailbox Password Control
        this.testMailboxPasswordControl.setValidators(this.fcListValidation.getValidators('MailboxPassword'));
        this.testMailboxPasswordControl.setValue(this.listItem.InterfaceDetails?.TestModeSettings?.MailboxPassword);

    }
}
